import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';

const IMAGE_MIME_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
  'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff',
];

interface DocumentThumbnailProps {
  documentId: number;
  mimeType: string;
  fileName: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClick?: () => void;
  rounded?: 'md' | 'lg' | 'xl';
}

export default function DocumentThumbnail({
  documentId,
  mimeType,
  fileName,
  className = '',
  size = 'md',
  onClick,
  rounded = 'lg',
}: DocumentThumbnailProps) {
  const { accessToken } = useAuth();
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  const isImage = IMAGE_MIME_TYPES.some((t) => mimeType?.toLowerCase().startsWith(t));

  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20',
  };

  const roundedClasses = {
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
  };

  useEffect(() => {
    if (!isImage) return;
    setLoading(true);
    setError(false);

    let revoked = false;
    let objectUrl: string | null = null;

    fetch(`${API_BASE}/documents/${documentId}/download?inline=true`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load image');
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setImgUrl(objectUrl);
      })
      .catch(() => {
        if (!revoked) setError(true);
      })
      .finally(() => {
        if (!revoked) setLoading(false);
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId, mimeType, accessToken, isImage]);

  const handleClick = useCallback(() => {
    if (onClick) onClick();
    if (isImage && imgUrl) setViewerOpen(true);
  }, [onClick, isImage, imgUrl]);

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
  }, []);

  // Keyboard escape to close viewer
  useEffect(() => {
    if (!viewerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeViewer();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [viewerOpen, closeViewer]);

  // File icon fallback
  if (!isImage || error) {
    return (
      <div
        className={`${sizeClasses[size]} ${roundedClasses[rounded]} bg-stone-100 flex items-center justify-center shrink-0 ${onClick ? 'cursor-pointer hover:bg-stone-200' : ''} ${className}`}
        onClick={handleClick}
      >
        <i className="ri-file-line text-stone-400 text-lg w-5 h-5 flex items-center justify-center" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`${sizeClasses[size]} ${roundedClasses[rounded]} bg-stone-100 flex items-center justify-center shrink-0 ${className}`}>
        <i className="ri-loader-4-line text-stone-400 text-lg animate-spin w-5 h-5 flex items-center justify-center" />
      </div>
    );
  }

  return (
    <>
      <div
        className={`${sizeClasses[size]} ${roundedClasses[rounded]} overflow-hidden bg-stone-100 flex items-center justify-center shrink-0 ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-brand-400' : ''} ${className}`}
        onClick={handleClick}
      >
        {imgUrl && (
          <img
            src={imgUrl}
            alt={fileName}
            className="w-full h-full object-cover"
            onError={() => setError(true)}
          />
        )}
      </div>

      {/* Full-screen image viewer */}
      {viewerOpen && imgUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8">
          <div
            className="absolute inset-0 bg-stone-900/85 backdrop-blur-sm"
            onClick={closeViewer}
          />
          <div className="relative z-10 max-w-5xl max-h-[92vh] w-full flex flex-col items-center">
            <div className="flex items-center justify-between w-full mb-4 px-1">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <i className="ri-image-line text-white text-sm w-4 h-4 flex items-center justify-center" />
                </div>
                <p className="text-white text-sm font-medium truncate">{fileName}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={`${API_BASE}/documents/${documentId}/download?inline=true`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                  title="Open in new tab"
                >
                  <i className="ri-external-link-line text-sm w-4 h-4 flex items-center justify-center" />
                </a>
                <button
                  onClick={closeViewer}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
                </button>
              </div>
            </div>
            <div className="relative w-full flex items-center justify-center overflow-hidden rounded-xl">
              <img
                src={imgUrl}
                alt={fileName}
                className="max-w-full max-h-[80vh] object-contain rounded-xl"
              />
            </div>
            <p className="text-white/50 text-xs mt-3">Press Escape or click outside to close</p>
          </div>
        </div>
      )}
    </>
  );
}